import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, Input } from '@angular/core';
import { CreationMode } from '@components/storage-accounts/add-storage-account-wizard/add-storage-account-wizard.constants';
import { StorageFormName } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/credential-forms.constants';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { isNil } from 'lodash';
import { AlertComponent, CardComponent, CardGroupComponent, CardHeaderComponent, MbsPopupType, TextEllipsisComponent } from 'mbs-ui-kit';
import { ftpStorageTypes } from 'mbs-ui-shared@constants/storage.constants';
import { StorageType } from 'mbs-ui-shared@models';
import { BucketInfo } from 'mbs-ui-shared@models/StorageAccount';
import { BoolDisplayPipe } from 'mbs-ui-shared@utils/pipes/bool-display.pipe';
import { StorageDisplayNamePipe } from 'mbs-ui-shared@utils/pipes/storage-display-name.pipe';
import { StorageTypeIconPipe } from 'mbs-ui-shared@utils/pipes/storage-type-icon.pipe';

@UntilDestroy()
@Component({
  selector: 'mbs-summary-step',
  templateUrl: './summary-step.component.html',
  styleUrls: ['./summary-step.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlertComponent,
    CardComponent,
    CardGroupComponent,
    CardHeaderComponent,
    CommonModule,
    TextEllipsisComponent,
    BoolDisplayPipe,
    StorageDisplayNamePipe,
    StorageTypeIconPipe,
    I18NextPipe
  ]
})
export class SummaryStepComponent {
  private i18N = inject(I18NextPipe);

  public readonly googleCloudAccounts = [StorageType.GoogleCloud, StorageType.Google];
  public readonly isNil = isNil;
  public readonly creationMode = CreationMode;
  public readonly mbsPopupType = MbsPopupType;
  public readonly storageTypeEnum = StorageType;

  formValue = input<any>();
  currentBucket = input<BucketInfo>();
  s3AdditionalOptionsShown = input<boolean>(false);
  useExistingAccount = input<boolean>(false);
  @Input() error: string;
  @Input() mode: CreationMode;
  @Input() credentialsFormName: string;

  public getSpecificBucketsNames(): string {
    return (this.formValue()?.arnRoleOptions?.specificBuckets || [])?.map((tag) => tag.name)?.join(', ');
  }

  get isNewBucket() {
    return this.formValue()?.createNew;
  }

  get storageType() {
    return this.formValue()?.storageType;
  }

  get cloudStorageId(): boolean {
    return this.formValue()?.storageAccount?.cloudStorageId;
  }

  get selectedCompanies(): string[] {
    return this.formValue()?.destination?.companies?.map(({ name }) => name);
  }

  get s3Accelerator(): boolean {
    return this.formValue()?.destination?.s3Accelerator;
  }

  get googleSubregion(): string {
    return this.isNewBucket ? this.formValue()?.destination?.googleDestinationSubregion?.displayName : null;
  }

  get storageClass(): string {
    return this.isNewBucket ? this.formValue()?.destination?.storageClass?.name : null;
  }

  get immutability(): boolean {
    return this.formValue()?.destination?.immutability;
  }

  get region(): string {
    return this.isNewBucket ? this.formValue()?.destination?.region?.displayName : this.currentBucket()?.region?.displayName;
  }

  get credentialFormValue() {
    const formName = StorageFormName[this.storageType];

    return this.useExistingAccount()
      ? this.formValue()?.storageAccount?.credential?.[formName]
      : this.formValue()?.credentialsInfo?.[formName];
  }

  get accountDisplayName(): string {
    return this.useExistingAccount() ? this.formValue()?.storageAccount?.displayName : this.formValue()?.accountDisplayName;
  }

  get s3CompatibleFlags() {
    return !this.s3AdditionalOptionsShown() && !this.useExistingAccount() && this.storageType === StorageType?.S3Compatible
      ? this.formValue()?.s3CompatibleFlags
      : this.credentialFormValue;
  }

  get useVHostRequestStyle(): boolean {
    return this.s3CompatibleFlags?.useVHostRequestStyle;
  }

  get accessStyle(): string {
    return this.useVHostRequestStyle
      ? this.i18N.transform('storages:addStorageAccountWizard.inputCredentialsStep.labels.hostStyle')
      : this.i18N.transform('storages:addStorageAccountWizard.inputCredentialsStep.labels.pathStyle');
  }

  get ignoreCertificate(): boolean {
    return this.s3CompatibleFlags?.ignoreCertificate;
  }

  get keepCredentialsOnEndpoint(): boolean {
    return this.s3CompatibleFlags?.keepCredentialsOnEndpoint;
  }

  get isSyntheticBackupAllowed(): boolean {
    return this.s3CompatibleFlags?.isSyntheticBackupAllowed;
  }

  get isVersioningEnabled(): boolean {
    return this.s3CompatibleFlags?.isVersioningEnabled;
  }

  get useSignatureVersion4(): boolean {
    return this.s3CompatibleFlags?.useSignatureVersion4;
  }

  getBucketType(): string {
    const storageType = this.storageType;

    if (storageType === StorageType.LocalFS) {
      return this.i18N.transform('shared:common.path');
    } else if (ftpStorageTypes.includes(storageType)) {
      return this.i18N.transform('shared:common.rootFolder');
    } else {
      return this.i18N.transform('shared:common.bucket');
    }
  }
}
